//
// types
//

export type Func = (...args: any[]) => any;

export interface Cache<K, V> {
  create: CacheCreateFunc<K, V>;
}

export type CacheCreateFunc<K, V> = () => {
  get(key: K): V;
  set(key: K, value: V): void;
  has(key: K): boolean;
};

export type Serializer = (args: any[]) => string;

export interface Options<F extends Func> {
  cache?: Cache<string, ReturnType<F>>;
  serializer?: Serializer;
  strategy?: MemoizeFunc;
}

export type MemoizeFunc = <F extends Func>(fn: F, options?: Options<F>) => F;

export interface Memoize extends MemoizeFunc {
  strategies: {
    variadic: MemoizeFunc;
    monadic: MemoizeFunc;
  };
}

//
// main
//

export function memoize<F extends Func>(fn: F, options?: Options<F>): F {
  const cache = options && options.cache ? options.cache : cacheDefault;

  const serializer = options && options.serializer ? options.serializer : serializerDefault;

  const strategy = options && options.strategy ? options.strategy : strategyDefault;

  return strategy(fn, {
    cache,
    serializer,
  });
}

//
// strategy
//

function isPrimitive(value: any) {
  return value == null || typeof value === 'number' || typeof value === 'boolean'; // || typeof value === "string" 'unsafe' primitive for our needs
}

function monadic(fn: any, cache: any, serializer: any, arg: any) {
  const cacheKey = isPrimitive(arg) ? arg : serializer(arg);

  let computedValue = cache.get(cacheKey);
  if (typeof computedValue === 'undefined') {
    computedValue = fn.call(this, arg);
    cache.set(cacheKey, computedValue);
  }

  return computedValue;
}

function variadic(fn: any, cache: any, serializer: any) {
  // eslint-disable-next-line prefer-rest-params
  const args = Array.prototype.slice.call(arguments, 3);
  const cacheKey = serializer(args);

  let computedValue = cache.get(cacheKey);
  if (typeof computedValue === 'undefined') {
    computedValue = fn.apply(this, args);
    cache.set(cacheKey, computedValue);
  }

  return computedValue;
}

function assemble(fn: any, context: any, strategy: any, cache: any, serialize: any) {
  return strategy.bind(context, fn, cache, serialize);
}

function strategyDefault(fn: any, options: any) {
  const strategy = fn.length === 1 ? monadic : variadic;

  return assemble(fn, this, strategy, options.cache.create(), options.serializer);
}

function strategyVariadic(fn: any, options: any) {
  const strategy = variadic;

  return assemble(fn, this, strategy, options.cache.create(), options.serializer);
}

function strategyMonadic(fn: any, options: any) {
  const strategy = monadic;

  return assemble(fn, this, strategy, options.cache.create(), options.serializer);
}

//
// serializer
//

export function serializerDefault(...args: any) {
  return JSON.stringify(args);
}

//
// cache
//

export const cacheDefault = {
  create: function create() {
    return new Map();
  },
};

//
// api
//

export const strategies = {
  variadic: strategyVariadic,
  monadic: strategyMonadic,
};
